import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { FormSelect } from '@aimsos/ui';

import { humanizeKey } from '../utils/humanizeKey.js';
import { NON_FIELD_SUPPORT, enumOf, supportFor } from '../utils/nonFieldBindings.js';
import BindingSlotLabel from './BindingSlotLabel.jsx';

/**
 * A binding that binds no field (fix/widgets).
 *
 * Two outcomes, both of them visible:
 *
 * - **A plain enum** (`CORRELATION.point_mode`, `NEXT_BEST_ACTION.source_mode`)
 *   renders as the select any `display` enum renders as, placeholder on the
 *   schema's own default.
 * - **Anything else** (`comparisons`, `relationships`, `context_queries`) is
 *   drawn with a red mark and a sentence saying the builder cannot author it
 *   yet. It is not hidden: the catalog declares the field, so the author sees
 *   it exists and knows why it is empty. None of them is required by any
 *   type, so nothing is blocked by leaving it unset.
 */
const NonFieldBindingRow = memo(function NonFieldBindingRow({ slot, value, onChange }) {
  const { t } = useTranslation('dashboards');
  const support = supportFor(slot);
  const choices = enumOf(slot);

  const options = useMemo(
    () => (choices ?? []).map((choice) => ({
      value: choice,
      label: t(`widgetBuilder.bindingSlots.values.${choice}`, {
        defaultValue: humanizeKey(choice),
      }),
    })),
    [choices, t],
  );

  const handleSelect = useCallback((next) => onChange(slot.key, next), [onChange, slot.key]);

  if (support === NON_FIELD_SUPPORT.ENUM) {
    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <BindingSlotLabel slotKey={slot.key} required={slot.required} />
        <FormSelect
          value={value ?? ''}
          onChange={handleSelect}
          options={options}
          placeholder={t('widgetBuilder.displaySettings.placeholder')}
          portalListbox
        />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <BindingSlotLabel slotKey={slot.key} required={slot.required} unsupported />
      <p className="m-0 rounded-lg border border-dashed border-edge px-3 py-2.5 text-[11px] leading-snug text-content-muted">
        {t(`widgetBuilder.bindingSlots.unsupportedBy.${slot.key}`, {
          defaultValue: t('widgetBuilder.bindingSlots.unsupported'),
        })}
      </p>
    </div>
  );
});

NonFieldBindingRow.propTypes = {
  /** One entry of `readSlotModel().nonFieldSlots`. */
  slot: PropTypes.shape({
    key: PropTypes.string.isRequired,
    required: PropTypes.bool,
    node: PropTypes.object,
  }).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

/**
 * The record key: a slot that accepts `UUID` and nothing else.
 *
 * There is exactly one candidate — the record uuid the backend adds to the
 * dataset it generates — so asking would be a question with one answer. It is
 * drawn anyway, read-only, because the catalog declares it and an author
 * comparing the contract with this form should find every key of it here.
 */
export const AutoBindingRow = memo(function AutoBindingRow({ slotKey, required }) {
  const { t } = useTranslation('dashboards');

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <BindingSlotLabel slotKey={slotKey} required={required} auto />
      <p className="m-0 rounded-lg border border-edge bg-surface-muted px-3 py-2.5 text-[11px] leading-snug text-content-muted">
        {t('widgetBuilder.bindingSlots.recordKeyNote')}
      </p>
    </div>
  );
});

AutoBindingRow.propTypes = {
  slotKey: PropTypes.string.isRequired,
  required: PropTypes.bool,
};

export default NonFieldBindingRow;
